import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { commandeDuRang, estPremierRang } from '../domain/rangs';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../../composants/Button/Button';
import DescriptifAdministratifBC from './DescriptifAdministratifBC';
import ModalArticles from '../../../composants/ModalArticles/ModalArticles';
import ListePiecesCard from '../../../composants/ListePiecesCard/ListePiecesCard';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import CGVAccept from '../../../composants/CGVAccept/CGVAccept';
import Banner from '../../../composants/Banner/Banner';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import TitulairesLotCards from '../../../composants/TitulairesLotCards/TitulairesLotCards';
import { useDocuments } from '../../../contexts/DocumentsContext';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import ArticlesService, { Article } from '../../../services/articlesService';
import chiffrageService from '../../../services/chiffrageService';
import { useAuth } from '../../../contexts/AuthContext';
import { isSignataireRole } from '../../../utils/roleUtils';
import { formatPrice, getStatusColor, formatDate } from '../../../utils/entityUtils';
import { useLotImage } from '../../../utils/getLotImage';
import { StatusId, OperationBC, Signataire } from '../types';
import { computeConsultationActions, operationActionsInternals } from '../domain/operationActions';
import { NextTabRender, hasConsultationWorkflowAction } from '../domain/operationNextTab';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import {
  formatExistingArticles,
  isConsultationSectionReadOnly,
  computeConsultationStatus,
  validateConsultationForm,
  buildConsultationSaveBody,
  buildConsultationFormData,
  mapApiErrorsToForm,
} from '../domain/operationConsultation';
import {
  saveOperationDescriptif,
  updateArticleQuantity,
  removeArticle,
  removeAllArticles,
  transfertPrestataire,
  lancerConsultationRang,
  annulerOperation,
  notifierResponsable,
  loadSignataires,
  ConsultationValidationError,
} from '../services/operationBcService';
import { loadContratTitulaire, titreContratTitulaire } from '../../../services/contratTitulaireService';
import type { ContratTitulaire } from '../../../services/contratTitulaireService';
import '../../../composants/OperationConsultationBC/OperationConsultationBC.css';
import { MENTION_VALIDITE_ACCORD_CADRE_BC } from '../../../utils/mentionsLegalesBC';

const { isExpired: isOperationExpired } = operationActionsInternals;

const scrollToFirstError = (): void => {
  setTimeout(() => {
    const el = document.querySelector('.error, .error-message');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 100);
};

// Longueur d'un code de chiffrage : 1 chiffre de rang + 7 caracteres tires d'un alphabet sans
// ambiguite (cf. bdc ChiffrageController::buildCode). Fixe par la generation, pas par l'affichage.
const CODE_LONGUEUR = 8;

// Panier vide : traite comme une erreur de FORMULAIRE (cle `articles`), pas comme un bandeau global.
// Le message s'affiche dans la section Descriptif Financier, la ou l'acheteur compose sa commande.
const ARTICLES_REQUIS_MESSAGE =
  'Vous devez choisir des articles, laisser le titulaire proposer un chiffrage, ou saisir un code de chiffrage avant de lancer la consultation.';

interface OperationConsultationProps {
  commande: OperationBC;
  rang: number;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setCommande: React.Dispatch<React.SetStateAction<OperationBC | null>>;
  handleLancerConsultation: (rang: number) => Promise<void>;
  handleAccepterCommande: (rang: number) => Promise<void>;
  handleRefuserCommande: (rang: number) => Promise<void>;
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: any;
  // CTA de navigation vers l'onglet suivant, resolu par OperationContent. L'argument dit si CETTE
  // barre propose deja un orange qui fait avancer le workflow : c'est la feuille qui le sait
  // (role signataire, chrono expire), pas le routeur.
  nextTabFor?: (hasWorkflowAction: boolean) => NextTabRender;
}

interface SelectedResponsable {
  id: number;
  type: string;
}

// Onglet Consultation acheteur-BC (V2) : preparation + lancement / transfert de la
// consultation d'un rang. Autonome : aucune dependance a l'ancienne grosse feuille.
// Boutons -> domain/operationActions ; derivations -> domain/operationConsultation ;
// appels API -> services/operationBcService.
const OperationConsultation: React.FC<OperationConsultationProps> = ({
  commande,
  rang,
  errors,
  setErrors,
  setCommande,
  handleLancerConsultation,
  handleAccepterCommande,
  handleRefuserCommande,
  acceptCGV = false,
  setAcceptCGV = () => { },
  printRef,
  nextTabFor,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSignataire = isSignataireRole(user?.role?.toString() ?? '');
  const lotImage = useLotImage(
    commande.operation?.lot_technique?.nom || commande.lot_technique?.nom,
  );

  // Etats UI
  const [isSaving, setIsSaving] = useState(false);
  // Feedback inline (remplace les alert()) : succes/erreur affiche en banniere en tete.
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [articlesMode, setArticlesMode] = useState<'initial' | 'catalogue' | 'prestataire'>('initial');
  // Voie demandee pendant la confirmation de changement : le clic ARME le changement, la modale le
  // confirme, et c'est seulement alors qu'on l'applique. Sans elle, `handleConfirmModeChange` ne
  // saurait pas vers quelle voie basculer (il remettait tout a `initial`).
  const [modeDemande, setModeDemande] = useState<'catalogue' | 'prestataire' | null>(null);
  const [existingArticles, setExistingArticles] = useState<Article[]>([]);

  // Voie 3 ("je dispose deja du chiffrage") : la saisie du code vit desormais au-dessus de l'apercu,
  // aux cotes des deux autres voies, et non plus enfouie dans la barre de recherche du catalogue.
  // L'input est toujours affiche — pas d'etat d'ouverture : la voie EST le champ.
  const [codeChiffrage, setCodeChiffrage] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [codeChiffrageError, setCodeChiffrageError] = useState<string | null>(null);

  const [isArticlesModalOpen, setIsArticlesModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isChangeModeModalOpen, setIsChangeModeModalOpen] = useState(false);
  const [isConfirmConsultationModalOpen, setIsConfirmConsultationModalOpen] = useState(false);
  const [pendingRangToConsult, setPendingRangToConsult] = useState<number | null>(null);

  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToLaunchAfterCGV, setReadyToLaunchAfterCGV] = useState(false);

  const [isNotifyResponsableModalOpen, setIsNotifyResponsableModalOpen] = useState(false);
  const [responsables, setResponsables] = useState<Signataire[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState<SelectedResponsable | null>(null);
  const [responsablesLoading, setResponsablesLoading] = useState(false);

  const [chronoExpired, setChronoExpired] = useState(false);

  // Validation live : activee des la premiere tentative echouee (lancement / transfert). Ensuite chaque
  // changement de champ re-valide -> l'erreur disparait des que le champ est rempli, reapparait s'il est
  // revide. Avant la 1re tentative, on n'affiche RIEN (pas d'erreurs sur des champs jamais touches).
  const [liveValidate, setLiveValidate] = useState(false);

  useEffect(() => {
    const handler = () => setChronoExpired(true);
    window.addEventListener('chronoExpired', handler);
    return () => window.removeEventListener('chronoExpired', handler);
  }, []);

  useEffect(() => {
    if (!liveValidate) return;
    setErrors(computeAllErrors());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commande.formulaire, existingArticles, articlesMode, liveValidate]);

  // Charge les articles du rang depuis la donnee (source : commande.commandes[rang].articles).
  // Deps limitees a (id, commandes, rang) volontairement : une edition de champ du formulaire
  // mute `formulaire` mais pas `commandes` ; elle ne doit donc PAS recharger la table et ecraser
  // les articles ajoutes via la modale (non encore refetches dans commande.commandes).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loaded = formatExistingArticles(commande, rang);
    setExistingArticles(loaded);
    setArticlesMode((prev) => (loaded.length > 0 ? 'catalogue' : prev === 'prestataire' ? 'prestataire' : 'initial'));
  }, [commande.id, commande.commandes, rang]);

  const expired = useMemo(() => isOperationExpired(commande, chronoExpired), [commande, chronoExpired]);
  const hasExistingArticles = existingArticles.length > 0;

  // Commande du rang consulte : elle porte le titulaire de ce rang (entite, contrat), ses articles et
  // le pivot lot_contrat qui donne SES prix. Toute lecture "du rang" part d'elle, jamais d'un index.
  const commandeCeRang = useMemo(() => commandeDuRang(commande, rang), [commande, rang]);

  // Etat du bouton "Charger" de la voie du code. SOURCE UNIQUE : il pilote a la fois l'attribut
  // `disabled` du bouton et l'apparence de tout l'objet (l'input doit se faner avec lui, sinon la
  // soudure se voit). Un code incomplet ne peut pas etre valide : inutile d'aller le demander au
  // serveur pour s'entendre repondre non.
  const codeButtonDisabled = applyingCode || codeChiffrage.length !== CODE_LONGUEUR;

  // Requete catalogue de l'APERCU. MEMOISEE : l'effet de chargement de ModalArticles a `commandes`
  // dans ses deps -> une nouvelle reference a chaque rendu le relancerait en boucle.
  // Les prix sont ceux du pivot lot_contrat de la commande CONSULTEE (son `id_contrat`), comme dans la
  // modale de composition. L'ancienne requete par (lot, departement) faisait resoudre tous les
  // titulaires du lot et retenait le meilleur rang : sur une operation sans rang 1, l'apercu affichait
  // les prix d'un titulaire qui n'est pas consulte.
  const cataloguePreviewCommandes = useMemo(
    () => (commandeCeRang?.id_contrat ? [commandeCeRang] : []),
    [commandeCeRang],
  );
  const adminReadOnly = useMemo(() => isConsultationSectionReadOnly(commande, expired), [commande, expired]);
  const consultationStatus = useMemo(() => computeConsultationStatus(commande), [commande]);
  const formData = useMemo(() => buildConsultationFormData(commande), [commande]);

  // Descriptif procedural (recap lecture seule, parite MS) : lots, date limite, pieces de l'accord-cadre.
  const { commonDocuments, loading: acDocsLoading } = useDocuments();

  // Contrat du titulaire du rang consulte. Il vient EN TETE des pieces : c'est la piece qui lie ce
  // titulaire-la a cet accord-cadre, donc le contexte des CCTP qui suivent.
  const [contratTitulaire, setContratTitulaire] = useState<ContratTitulaire | null>(null);
  const lotContratRang = commandeCeRang?.id_contrat;

  useEffect(() => {
    if (!lotContratRang) {
      setContratTitulaire(null);
      return undefined;
    }
    let annule = false;
    loadContratTitulaire(lotContratRang)
      .then((contrat) => {
        if (!annule) setContratTitulaire(contrat);
      })
      .catch(() => {
        // Piece d'appoint : son echec ne doit pas priver l'acheteur des CCTP, qui eux sont la.
        if (!annule) setContratTitulaire(null);
      });
    return () => {
      annule = true;
    };
  }, [lotContratRang]);

  const piecesAccordCadre = useMemo(() => {
    const communs = commonDocuments
      .filter((d) => d.actif)
      .map((d) => ({ id: d.id, type: d.type, updated_at: d.updated_at, displayLabel: d.nom, category: 'commun' as const }));

    // Sans binaire, pas de carte : mieux vaut ne rien afficher qu'un bouton qui telechargerait du vide.
    if (!contratTitulaire?.binaire) return communs;

    return [
      {
        id: contratTitulaire.id,
        type: 'Contrat',
        updated_at: contratTitulaire.updated_at ?? '',
        displayLabel: titreContratTitulaire(contratTitulaire),
        category: 'accord_cadre' as const,
        // `key` distingue cette carte des documents communs, qui sont identifies par leur seul `id` :
        // sans elle, un document commun de meme id serait confondu avec le contrat.
        key: `contrat-${contratTitulaire.id}`,
        binaire: contratTitulaire.binaire,
      },
      ...communs,
    ];
  }, [commonDocuments, contratTitulaire]);
  const procLotTech = commande.operation?.lot_technique || commande.lot_technique;
  const procDep = commande.operation?.lot_departement?.departement || commande.lot_departement?.departement;
  const procLotTechnique = procLotTech ? `${procLotTech.id ?? ''} - ${procLotTech.nom ?? ''}` : '';
  const procLotGeographique = procDep ? `${procDep.id ?? ''} - ${procDep.nom ?? ''}` : '';
  const procDateLimite = commande.expire_at
    ? `${new Date(commande.expire_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} (GMT Paris)`
    : '';

  const actions = useMemo(
    () =>
      computeConsultationActions(commande, rang, {
        isSignataire,
        modePrestataire: articlesMode === 'prestataire',
        chronoExpired,
      }),
    [commande, rang, isSignataire, articlesMode, chronoExpired],
  );

  // Notification de demande de validation deja envoyee (prefix 'Demande de validation').
  const notificationInfo = useMemo(() => {
    const operationId = commande.id;
    const historique = commande.historique || [];
    if (!operationId) return { sent: false, labels: [] as string[] };
    const events = historique.filter(
      (e) => e.prefix === 'Demande de validation' && e.type === `BDC.Operation.${operationId}`,
    );
    if (events.length > 0) {
      const labels = events
        .slice()
        .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
        .map((e) => e.label)
        .filter((l): l is string => l !== null && l !== undefined);
      return { sent: true, labels };
    }
    return { sent: false, labels: [] as string[] };
  }, [commande]);

  const hasArticles = useCallback(
    (): boolean => existingArticles.length > 0 || articlesMode === 'prestataire',
    [existingArticles, articlesMode],
  );

  // Etat COMPLET des erreurs : champs du descriptif + composition du panier.
  //
  // L'absence d'article etait traitee a part, en bandeau rouge, avec un `return` AVANT la validation
  // des champs : l'acheteur qui n'avait rien rempli ne voyait qu'un seul reproche a la fois et devait
  // les decouvrir un par un. C'est desormais une erreur de formulaire comme les autres, cumulee avec
  // le reste et rendue a l'endroit qu'elle concerne (section Descriptif Financier).
  const computeAllErrors = useCallback((): Record<string, string> => {
    const tous = { ...validateConsultationForm(formData).errors };
    if (!hasArticles()) tous.articles = ARTICLES_REQUIS_MESSAGE;
    return tous;
  }, [formData, hasArticles]);

  // --- Mutations du formulaire ------------------------------------------------
  const handleFormDataChange = useCallback(
    (newData: Record<string, unknown>) => {
      setCommande((prev) =>
        prev ? { ...prev, formulaire: { ...prev.formulaire, ...newData } } : prev,
      );
    },
    [setCommande],
  );

  // Arrivee depuis le CATALOGUE apres creation d'un BC : on amene l'acheteur directement au
  // "Descriptif Financier" du rang 1, la ou il compose sa commande — plutot que de le laisser en haut
  // d'une page qu'il devrait scroller. Le hash porte l'intention (#descriptif-financier).
  //
  // `requestAnimationFrame` : la section n'existe pas encore au moment ou l'effet se declenche (le
  // rendu de l'onglet suit). Un scroll immediat ne trouverait rien. On attend donc la frame suivante.
  // Le hash est consomme UNE fois (on le retire de l'URL) : sans ca, un simple re-rendu rescrollerait
  // et l'acheteur ne pourrait plus remonter la page.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== '#descriptif-financier') return undefined;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector('.bc-ancre-financier')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      navigate({ search: window.location.search }, { replace: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash, navigate]);

  // Materialise le defaut "Public (P)" du niveau de sensibilite dans la SOURCE (commande.formulaire),
  // pas seulement dans l'affichage. Sans ca : le select montre "Public (P)" (via formData, qui porte
  // le defaut), mais `commande.formulaire.niveauSensibilite` reste vide tant que l'utilisateur ne
  // clique pas -> au lancement, la validation ET la sauvegarde lisent commande.formulaire et
  // persistent NULL. On ecrit donc le defaut des que le formulaire est editable et le champ absent.
  // La condition retombe a false au tour suivant (le champ n'est plus vide) : pas de boucle.
  useEffect(() => {
    if (!adminReadOnly && !commande.formulaire?.niveauSensibilite) {
      handleFormDataChange({ niveauSensibilite: 'Public (P)' });
    }
  }, [adminReadOnly, commande.formulaire?.niveauSensibilite, handleFormDataChange]);

  const handleBesoinChange = useCallback(
    (value: string) => {
      setCommande((prev) =>
        prev ? { ...prev, formulaire: { ...prev.formulaire, besoin: value } } : prev,
      );
    },
    [setCommande],
  );

  // --- Sauvegarde -------------------------------------------------------------
  const saveOperationData = useCallback(
    async (validateRequired: boolean): Promise<void> => {
      if (validateRequired) {
        const v = validateConsultationForm(formData);
        if (!v.valid) {
          setErrors(v.errors);
          throw new Error('Veuillez remplir tous les champs obligatoires');
        }
      }
      try {
        await saveOperationDescriptif(commande.id, buildConsultationSaveBody(commande));
        setErrors({});
      } catch (error) {
        if (error instanceof ConsultationValidationError) {
          setErrors(mapApiErrorsToForm(error.validationErrors));
          scrollToFirstError();
          throw new Error('Erreurs de validation détectées');
        }
        throw error;
      }
    },
    [commande, formData, setErrors],
  );

  // --- Articles (CRUD) --------------------------------------------------------
  const handleAddToCommand = useCallback((articles: Article[]) => {
    setExistingArticles(articles);
    setArticlesMode(articles.length > 0 ? 'catalogue' : 'initial');
  }, []);

  // --- Application d'un code de chiffrage (devis prestataire pre-etabli) -------
  // Le back (apply-chiffrage-code) valide lot/departement, titularite et cascade de rang, puis pre-remplit
  // le catalogue de l'operation cote serveur (source de verite, sans consommer le code) et RENVOIE
  // l'operation a jour (bdc storeCatalogue -> OperationResource complet, relaye tel quel par core).
  // On applique donc cette reponse a l'etat : aucun rechargement, les articles apparaissent tout de
  // suite. Recharger la page aurait redemande au serveur ce qu'il venait de nous donner.
  // Un echec metier remonte via throw pour affichage inline (le service ne throw pas de lui-meme).
  const handleAppliquerCode = useCallback(
    async (code: string): Promise<void> => {
      const result = await chiffrageService.applyCode(commande.id, code);
      if (result.status === 'error') {
        throw new Error(result.message || 'Ce code de chiffrage est invalide.');
      }

      const operationAJour = result.data as Partial<OperationBC> | undefined;
      if (!operationAJour?.commandes) {
        // Le serveur a bien applique le code mais ne nous rend pas l'operation : plutot que d'afficher
        // un ecran qui ment (articles absents alors qu'ils existent), on retombe sur le rechargement.
        window.location.reload();
        return;
      }

      // `commandes` porte les articles : l'effet de chargement (deps : id, commandes, rang) recalcule
      // seul `existingArticles` et bascule articlesMode -> l'apercu laisse place au catalogue rempli.
      setCommande((prev) =>
        prev
          ? { ...prev, ...operationAJour, formulaire: { ...prev.formulaire, ...operationAJour.formulaire } }
          : prev,
      );
      setCodeChiffrage('');
      setFeedback({ type: 'success', message: 'Le chiffrage du titulaire a été chargé.' });
    },
    [commande.id, setCommande],
  );

  // Voie 3 : appliquer le code remplit reellement l'operation avec l'offre du titulaire — l'apercu
  // laisse alors place au catalogue charge (le rechargement de handleAppliquerCode s'en charge).
  const handleChargerChiffrage = useCallback(async () => {
    const code = codeChiffrage.trim();
    if (!code || applyingCode) return;
    setApplyingCode(true);
    setCodeChiffrageError(null);
    try {
      await handleAppliquerCode(code);
    } catch (error) {
      setCodeChiffrageError(
        error instanceof Error ? error.message : 'Ce code de chiffrage est invalide.',
      );
    } finally {
      // `finally` et non le seul `catch` : du temps ou le succes rechargeait la page, l'etat mourait
      // avec elle. Sans rechargement, un succes laissait `applyingCode` a true pour toujours — et
      // l'input, disabled dessus, devenait mort des que les trois voies revenaient.
      setApplyingCode(false);
    }
  }, [codeChiffrage, applyingCode, handleAppliquerCode]);

  const handleExistingQuantityChange = useCallback(
    async (index: number, quantity: number) => {
      const validQuantity = Math.max(1, quantity);
      const article = existingArticles[index];
      if (!article) return;

      const updated = existingArticles.map((art, i) => {
        if (i !== index) return art;
        const prixUnitaire = ArticlesService.getPrixByQuantity(art, validQuantity);
        return { ...art, quantite: validQuantity, totalHT: prixUnitaire * validQuantity };
      });
      setExistingArticles(updated);

      try {
        const catalogueId = article.catalogueId || article.id;
        await updateArticleQuantity(commande.id, catalogueId, validQuantity);
      } catch {
        // Sauvegarde automatique silencieuse : pas d'alerte utilisateur.
      }
    },
    [existingArticles, commande.id],
  );

  const handleRemoveExistingArticle = useCallback(
    async (catalogueId: string) => {
      setDeletingId(catalogueId);
      try {
        await removeArticle(commande.id, catalogueId);
        const updated = existingArticles.filter(
          (a) => a.catalogueId !== catalogueId && a.id !== catalogueId,
        );
        setExistingArticles(updated);
        if (updated.length === 0) setArticlesMode('initial');
      } catch (error) {
        setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de la suppression de l'article" });
      } finally {
        setDeletingId(null);
      }
    },
    [existingArticles, commande.id],
  );

  // --- Lancement --------------------------------------------------------------
  const handleConfirmLaunch = useCallback(() => setIsConfirmModalOpen(true), []);

  const handleLancer = useCallback(async () => {
    // Un SEUL passage de validation, cumulant champs et panier : l'ancien code sortait des l'absence
    // d'article, si bien que les champs obligatoires vides n'etaient meme pas evalues. L'acheteur
    // corrigeait un reproche pour en decouvrir un autre au clic suivant.
    const tousLesErreurs = computeAllErrors();
    if (Object.keys(tousLesErreurs).length > 0) {
      setLiveValidate(true);
      setErrors(tousLesErreurs);
      // Amene au PREMIER probleme dans l'ordre de la page — un champ du descriptif s'il y en a,
      // sinon la section Descriptif Financier qui porte l'erreur de panier.
      scrollToFirstError();
      return;
    }
    try {
      setIsSaving(true);
      await saveOperationData(true);
      setIsSaving(false);
      if (!acceptCGV) {
        setReadyToLaunchAfterCGV(true);
        setIsCGVModalOpen(true);
      } else {
        handleConfirmLaunch();
      }
    } catch (error) {
      setIsSaving(false);
      const message = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';
      if (message !== 'Erreurs de validation détectées') {
        setFeedback({ type: 'danger', message: 'Erreur lors de la sauvegarde: ' + message });
      }
      scrollToFirstError();
    }
    // `computeAllErrors` remplace `hasArticles` + `formData` : il les capture tous les deux.
  }, [computeAllErrors, setErrors, saveOperationData, acceptCGV, handleConfirmLaunch]);

  const handleConfirmLancerConsultation = useCallback(async () => {
    try {
      await saveOperationData(false);
      await handleLancerConsultation(rang);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setFeedback({ type: 'danger', message: `Erreur lors du lancement de la consultation: ${message}` });
    }
  }, [saveOperationData, handleLancerConsultation, rang]);

  // --- Transfert au prestataire (panier inverse) ------------------------------
  const handleTransfertPrestataire = useCallback(async () => {
    const v = validateConsultationForm(formData);
    if (!v.valid) {
      setLiveValidate(true);
      setErrors(v.errors);
      scrollToFirstError();
      return;
    }
    try {
      await saveOperationData(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la sauvegarde';
      if (message === 'Erreurs de validation détectées') {
        scrollToFirstError();
      } else {
        setFeedback({ type: 'danger', message: `Erreur lors de la sauvegarde: ${message}` });
      }
      return;
    }

    try {
      const result = await transfertPrestataire(commande.id);
      const data = result.data as Partial<OperationBC> | undefined;
      if (data) {
        setCommande((prev) =>
          prev ? { ...prev, ...data, formulaire: { ...prev.formulaire, ...data.formulaire } } : prev,
        );
      } else {
        setCommande((prev) => (prev ? { ...prev, transferred: true } : prev));
      }
      setFeedback({ type: 'success', message: 'Demande envoyée au prestataire avec succès. Il définira les articles nécessaires.' });
      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors du transfert au prestataire';
      setFeedback({ type: 'danger', message: `Erreur: ${message}. Veuillez réessayer.` });
    }
  }, [commande, formData, setErrors, saveOperationData, setCommande]);

  // "Changer de mode" ne transfere PLUS au prestataire. Depuis que les trois voies de composition sont
  // offertes ensemble en amont, changer de mode = revenir a ce choix : on vide les articles et on rend
  // la main. Transferer d'emblee, c'etait trancher a la place de l'acheteur pour la seule voie
  // irreversible des trois — celle-ci garde sa propre confirmation.
  // Point d'engagement UNIQUE de l'operation : quel que soit le mode de composition, rien ne part
  // avant ce clic. Il route ensuite selon le mode — le transfert au titulaire n'est plus une action a
  // part declenchee par son propre bouton, c'est ce que "lancer" VEUT DIRE quand l'acheteur a choisi
  // de laisser le titulaire proposer.
  const lancerSelonMode = useCallback(async () => {
    if (articlesMode === 'prestataire') {
      await handleTransfertPrestataire();
      return;
    }
    await handleConfirmLancerConsultation();
  }, [articlesMode, handleTransfertPrestataire, handleConfirmLancerConsultation]);

  // Demande de bascule vers une voie de composition. Tant qu'aucun article n'est compose, le
  // changement est gratuit et immediat. Des qu'un panier existe, basculer le detruit : on ARME la
  // demande et la modale tranche. Un clic sur la voie DEJA active ne fait rien — sans cette garde,
  // reouvrir le catalogue depuis son propre bouton aurait propose d'effacer le panier qu'on vient
  // de composer.
  const demanderMode = useCallback(
    (cible: 'catalogue' | 'prestataire') => {
      if (cible === articlesMode) {
        if (cible === 'catalogue') setIsArticlesModalOpen(true);
        return;
      }
      if (existingArticles.length > 0) {
        setModeDemande(cible);
        setIsChangeModeModalOpen(true);
        return;
      }
      setArticlesMode(cible);
      if (cible === 'catalogue') setIsArticlesModalOpen(true);
    },
    [articlesMode, existingArticles],
  );

  const handleConfirmModeChange = useCallback(async () => {
    setIsChangeModeModalOpen(false);
    setIsDeleting(true);
    try {
      await removeAllArticles(commande.id);
      // Etat local ET donnee source : l'effet de chargement relit `commande.commandes` et
      // reimposerait les articles supprimes si on ne purgeait que `existingArticles`.
      setCommande((prev) =>
        prev
          ? { ...prev, commandes: (prev.commandes || []).map((c) => ({ ...c, articles: [] })) }
          : prev,
      );
      setExistingArticles([]);
      setCodeChiffrage('');
      setCodeChiffrageError(null);
      // On bascule vers la voie DEMANDEE plutot que de revenir au choix initial : les trois voies
      // restant affichees en permanence, l'acheteur a deja designe sa destination en cliquant.
      setArticlesMode(modeDemande ?? 'initial');
      if (modeDemande === 'catalogue') setIsArticlesModalOpen(true);
      setModeDemande(null);
    } catch (error) {
      setFeedback({
        type: 'danger',
        message: error instanceof Error ? error.message : 'Erreur lors de la suppression des articles',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [commande.id, setCommande, modeDemande]);

  // --- Validation par un signataire -------------------------------------------
  const loadResponsables = useCallback(async () => {
    setResponsablesLoading(true);
    try {
      setResponsables(await loadSignataires());
    } catch {
      setResponsables([]);
    } finally {
      setResponsablesLoading(false);
    }
  }, []);

  const handleNotifyResponsable = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveOperationData(true);
      setIsNotifyResponsableModalOpen(true);
      loadResponsables();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';
      if (message === 'Erreurs de validation détectées' || message === 'Veuillez remplir tous les champs obligatoires') {
        scrollToFirstError();
      } else {
        setFeedback({ type: 'danger', message: message });
      }
    } finally {
      setIsSaving(false);
    }
  }, [saveOperationData, loadResponsables]);

  const handleConfirmNotifyResponsable = useCallback(async () => {
    if (!selectedResponsable) {
      setFeedback({ type: 'danger', message: 'Veuillez sélectionner un responsable' });
      return;
    }
    try {
      const result = await notifierResponsable(commande.id, selectedResponsable.id, selectedResponsable.type);
      setIsNotifyResponsableModalOpen(false);
      setSelectedResponsable(null);
      setFeedback({ type: 'success', message: `La demande a été envoyée avec succès à ${result.responsable?.prenom} ${result.responsable?.nom}.` });
      window.location.reload();
    } catch (error) {
      setFeedback({ type: 'danger', message: `Erreur lors de la demande au responsable: ${(error as Error).message}` });
    }
  }, [selectedResponsable, commande.id]);

  // --- Annulation / sortie ----------------------------------------------------
  const handleCancelOperation = useCallback(async () => {
    setIsDeleting(true);
    try {
      await annulerOperation(commande.id);
      navigate('/operations');
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de l'annulation de l'opération." });
    } finally {
      setIsDeleting(false);
      setIsCancelModalOpen(false);
    }
  }, [commande.id, navigate]);

  const handleSaveAndQuit = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveOperationData(false);
      navigate('/operations');
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.' });
    } finally {
      setIsSaving(false);
    }
  }, [saveOperationData, navigate]);

  // --- Consultation du rang suivant (apres refus) -----------------------------
  const handleConfirmConsultationRangSuivant = useCallback(async () => {
    setIsConfirmConsultationModalOpen(false);
    const rangToRun = pendingRangToConsult;
    setPendingRangToConsult(null);
    if (!rangToRun) return;
    try {
      if (commande.transferred === true) {
        await transfertPrestataire(commande.id);
      } else {
        await lancerConsultationRang(commande.id);
      }
      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors du lancement';
      setFeedback({ type: 'danger', message: `Erreur: ${message}. Veuillez réessayer.` });
    }
  }, [pendingRangToConsult, commande]);

  const isAnnule = commande.status?.id === StatusId.Annule;

  const printButton = (
    <PrintButton
      targetRef={printRef}
      fileName={`${commande.numero || commande.operation?.numero} - Consultation Rang ${rang}`}
      value="Imprimer la consultation"
      suiviHeaderProps={{
        titre: commande.operation?.nom || commande.nom || '',
        sousTitre: '',
        subtitle: (() => {
          const lt = commande.operation?.lot_technique || commande.lot_technique;
          const dep = commande.operation?.lot_departement?.departement || commande.lot_departement?.departement;
          return lt && dep ? `${lt.id} - ${lt.nom}, ${dep.id} - ${dep.nom}` : 'Informations de lot non disponibles';
        })(),
        status: commande.status?.nom || 'Statut inconnu',
        statusColor: getStatusColor(commande.status?.nom || ''),
        delais: commande.expire_at,
        backgroundImage: lotImage,
      }}
    />
  );

  // Les oranges de CETTE barre qui font avancer le workflow. Tant que l'un d'eux est propose,
  // c'est lui le CTA de l'onglet : le domaine masque alors la navigation.
  const nextTab = nextTabFor?.(hasConsultationWorkflowAction(actions));

  const showActionRow =
    actions.annuler.visible ||
    actions.sauvegarderQuitter.visible ||
    actions.refuserOffre.visible ||
    actions.accepterOffre.visible ||
    actions.consulterRangSuivant.visible ||
    actions.demanderValidation.visible ||
    actions.lancerConsultation.visible ||
    // Sans ca, l'onglet d'une operation figee (aucune action due) n'afficherait aucune barre,
    // donc aucune navigation : c'est precisement la ou elle est la seule sortie.
    Boolean(nextTab?.visible);

  // Banniere d'ETAT de l'onglet, definie UNE fois et rendue en HAUT ET EN BAS (meme dispositif que
  // les autres onglets BC). Deux copies de JSX finiraient par diverger.
  //
  // Recap « La consultation a ete lancee par … le … » : affiche des que la consultation a ete lancee
  // — voie catalogue/code OU transfert au titulaire — et PERSISTE ensuite. Le signal est la PRESENCE
  // de l'evenement d'historique "Consultation" (journalise par le core au lancement comme au
  // transfert), et non le seul cas transfert (`consultationStatus.isVisible`), qui laissait l'onglet
  // sans recap apres un lancement normal. Date et operateur LUS SUR CET EVENEMENT (parite MS
  // getLancementConsultationInfo) : stables apres acceptation, la ou `commande.updated_at` bascule
  // sur la date d'acceptation au statut Valide(7). L'operateur est celui que le core a inscrit sur
  // l'evenement : la PERSONNE qui a lance (prenom nom, depuis le profil du role), avec repli sur le
  // nom de l'organisme si le profil ne porte pas de nom.
  // Evenement de lancement DE CE RANG : le core estampille l'evenement 'Consultation' avec, en
  // `destinataire`, l'id de la commande du prestataire consulte pour ce rang (au lancement comme au
  // transfert). En BC multi-rang (rang 2 lance apres refus du rang 1), on isole ainsi l'evenement du
  // rang AFFICHE plutot que de prendre le plus recent de l'operation. Repli sur le plus recent pour
  // les evenements anterieurs a cette estampille (destinataire === 'systeme').
  const commandeRangId = commandeCeRang?.id;
  // Un rang encore en BrouillonAcheteur(1) n'a RIEN reçu — ni consultation, ni transfert : le module
  // fait sortir du brouillon la commande du rang servi dans les DEUX voies (lancement normal ->
  // AAccepter(4), transfert au titulaire -> AChiffrer(11)) et n'en sert qu'un a la fois. Meme signal
  // que useSuiviEntityTabs pour decider qu'un rang > 1 n'est pas encore lance.
  const rangPasEncoreLance = commandeCeRang?.id_status === StatusId.BrouillonAcheteur;
  const consultationEvents = (commande.historique || [])
    .filter((h) => h.prefix === 'Consultation')
    .slice()
    .sort(
      (a, b) =>
        new Date(b.created_at || b.updated_at || '').getTime() -
        new Date(a.created_at || a.updated_at || '').getTime(),
    );
  // L'evenement ESTAMPILLE pour ce rang fait foi en toutes circonstances : il prouve que ce rang a
  // ete servi, meme si sa commande est ensuite retombee en brouillon (cas observe sur une operation
  // expiree dont les rangs ont ete reinitialises).
  const evenementDeCeRang =
    commandeRangId != null
      ? consultationEvents.find((h) => String(h.destinataire) === String(commandeRangId))
      : undefined;
  // Le repli sur le plus recent ne couvre que les evenements ANTERIEURS a l'estampille par rang
  // (destinataire === 'systeme'). Il n'est legitime que si ce rang a effectivement ete servi : sinon
  // il empruntait l'evenement du rang PRECEDENT, et l'onglet Consultation du rang 2 annonçait
  // « La consultation a ete lancee par … le … » avec la date du rang 1, avant meme que l'acheteur
  // ait consulte ce rang.
  const lancementConsultationEvent =
    evenementDeCeRang ?? (rangPasEncoreLance ? undefined : consultationEvents[0]);
  // "JJ/MM/AAAA à HHhMM" : on reutilise formatDate (ancrage UTC -> heure locale correct) et on reduit
  // "à HH:MM:SS" en "à HHhMM" (sans secondes, separateur 'h'), format demande pour ce recap.
  const dateLancementConsultation = lancementConsultationEvent
    ? formatDate(lancementConsultationEvent.created_at || lancementConsultationEvent.updated_at || '').replace(
        /à (\d{2}):(\d{2}):\d{2}$/,
        'à $1h$2',
      )
    : null;
  const operateurLancement = lancementConsultationEvent?.operateur?.trim() || '';

  const stateBanners =
    lancementConsultationEvent && dateLancementConsultation ? (
      <Banner
        variant="success"
        filled
        message={
          operateurLancement
            ? `La consultation a été lancée par ${operateurLancement} le ${dateLancementConsultation}`
            : `La consultation a été lancée le ${dateLancementConsultation}`
        }
      />
    ) : null;

  // Confirmation de changement de voie : le texte NOMME la destination. L'ancien message annoncait
  // « vous reviendrez au choix du mode de composition » — un ecran qui n'existe plus depuis que les
  // trois voies restent affichees et que la bascule mene directement a celle qu'on a cliquee.
  const titreChangementMode =
    modeDemande === 'prestataire'
      ? 'Laisser le titulaire proposer un chiffrage'
      : 'Sélectionner les articles et les quantités';
  const messageChangementMode =
    modeDemande === 'prestataire'
      ? 'Attention, les articles que vous avez sélectionnés seront supprimés : c\'est le titulaire qui établira la proposition. Rien ne lui est envoyé tant que vous n\'avez pas lancé la consultation.'
      : 'Attention, les articles que vous avez sélectionnés seront supprimés et le catalogue s\'ouvrira pour une nouvelle composition.';

  const launchTooltip = !acceptCGV
    ? 'Vous devez accepter les CG'
    : !hasArticles()
      ? 'Vous devez sélectionner au moins un article'
      : actions.lancerConsultation.reason || '';

  return (
    <>
      <div className="consultation-bc">
        {feedback && <Banner variant={feedback.type} message={feedback.message} />}
        {stateBanners}
        {expired && !isAnnule && (
          <div
            className="expired-notice"
            style={{
              backgroundColor: '#fee',
              border: '2px solid #c33',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              color: '#c33',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            Cette commande est expirée. Vous ne pouvez plus modifier cette consultation.
          </div>
        )}

        {/* Descriptif Administratif */}
        <SectionPliable title="Descriptif Administratif" defaultOpen>
          <div className="section-content">
            <DescriptifAdministratifBC
              operation={commande}
              formData={formData}
              viewMode={adminReadOnly}
              errors={errors}
              setFormData={!adminReadOnly ? handleFormDataChange : undefined}
            />
          </div>
        </SectionPliable>

        {/* Descriptif Technique : layout identique MS (bandeau + 3 champs doc en grille + cahier des
            charges). L'attestation BPU du MS ne s'applique PAS a un bon de commande (on utilise le BPU). */}
        <SectionPliable title="Descriptif Technique" defaultOpen>
          <Banner
            variant="info"
            message="Les informations écrites ci-dessous sont contractuelles y compris les pièces éventuellement chargées au travers du lien URL. Mais, toutes précisions administratives ou juridiques de nature à contrevenir à l'accord-cadre général sans dérogations prévues au contrat sont à considérer comme inexistantes dans ce descriptif"
          />
          <div className="section-content">
            {/* Documentation technique : 3 champs optionnels, grille 12 colonnes (span 4). */}
            <div className="bcadm-desc bcadm-desc__doc-technique">
              <div className="bcadm-desc__grid">
                <div className="form-group" style={{ gridColumn: 'span 4' }}>
                  <label>URL vers la doc technique et/ou planning détaillé</label>
                  {adminReadOnly ? (
                    <div className="input-inactif">{(formData.urlDocTechnique as string | undefined) || '-'}</div>
                  ) : (
                    <textarea
                      value={(formData.urlDocTechnique as string | undefined) || ''}
                      onChange={(e) => handleFormDataChange({ urlDocTechnique: e.target.value })}
                      placeholder="ex : https://france-transfert-567890"
                      rows={3}
                      maxLength={1500}
                    />
                  )}
                  {!adminReadOnly && (
                    <span className="bcadm-char-count">{((formData.urlDocTechnique as string | undefined) || '').length}/1500</span>
                  )}
                </div>
                <div className="form-group" style={{ gridColumn: 'span 4' }}>
                  <label>Modalité d'accès à la documentation</label>
                  {adminReadOnly ? (
                    <div className="input-inactif">{(formData.modaliteAccesDocumentation as string | undefined) || '-'}</div>
                  ) : (
                    <textarea
                      value={(formData.modaliteAccesDocumentation as string | undefined) || ''}
                      onChange={(e) => handleFormDataChange({ modaliteAccesDocumentation: e.target.value })}
                      placeholder="ex : Nous contacter par mail pour obtenir le mot de passe"
                      rows={3}
                      maxLength={1500}
                    />
                  )}
                  {!adminReadOnly && (
                    <span className="bcadm-char-count">{((formData.modaliteAccesDocumentation as string | undefined) || '').length}/1500</span>
                  )}
                </div>
                <div className="form-group" style={{ gridColumn: 'span 4' }}>
                  <label>Liste des pièces éventuelles composant la documentation technique</label>
                  {adminReadOnly ? (
                    <div className="input-inactif">{(formData.listePiecesDocumentation as string | undefined) || '-'}</div>
                  ) : (
                    <textarea
                      value={(formData.listePiecesDocumentation as string | undefined) || ''}
                      onChange={(e) => handleFormDataChange({ listePiecesDocumentation: e.target.value })}
                      placeholder="ex : Planning détaillé, CCTP, plan du rez de chaussée, Coupe AA', Plan EP/EU, Charte spéciale BRUIT, etc…"
                      rows={3}
                      maxLength={1500}
                    />
                  )}
                  {!adminReadOnly && (
                    <span className="bcadm-char-count">{((formData.listePiecesDocumentation as string | undefined) || '').length}/1500</span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>
                Cahier des charges techniques et particularités à connaitre{' '}
                {!adminReadOnly && <span style={{ color: 'red' }}>*</span>}
              </label>
              {/* Lu depuis `formData` (vue fusionnee) et non depuis le buffer `formulaire` : celui-ci
                  est vide au chargement, donc le cahier des charges deja enregistre s'affichait vide
                  a la reouverture — et repartait vide au premier enregistrement. Le repli
                  `commande.operation?.besoin` du mode lecture ne couvrait que les reponses ou
                  l'operation est imbriquee, pas le cas courant. */}
              {adminReadOnly ? (
                <div className="input-inactif bcadm-besoin-readonly">
                  {formData.besoin || '-'}
                </div>
              ) : (
                <>
                  <textarea
                    value={formData.besoin || ''}
                    onChange={(e) => handleBesoinChange(e.target.value)}
                    className={`bcadm-besoin-textarea ${errors.besoin ? 'error' : ''}`}
                    placeholder="ex : les candidats sont informés qu'ils interviendront en site occupé…"
                    rows={20}
                    maxLength={30000}
                  />
                  <span className="bcadm-char-count">{(formData.besoin || '').length}/30000</span>
                  {errors.besoin && <span className="error-message">{errors.besoin}</span>}
                </>
              )}
            </div>
          </div>
        </SectionPliable>

        {/* Descriptif Financier */}
        {/* `bc-ancre-financier` : cible de l'arrivee depuis le catalogue (creation d'un BC), qui
            navigue avec le hash #descriptif-financier. Une classe plutot qu'un id : SectionPliable
            propage className, et l'ancre reste une donnee de mise en page, pas un identifiant global. */}
        <SectionPliable title="Descriptif Financier" className="bc-ancre-financier" defaultOpen>
          <div className="section-content">
            {/* Panier vide : le reproche s'affiche ICI, au-dessus des trois voies de composition, et
                non en bandeau global tout en haut de l'onglet — il porte sur ce qui se remplit dans
                cette section. `error-message` est la classe que cible scrollToFirstError. */}
            {errors.articles && (
              <span className="error-message bc-articles-error">{errors.articles}</span>
            )}
            {(() => {
              const isTransferred = commande.transferred === true;
              const sid = commande.status?.id;
              const isTransfertAcheteurValidation =
                isTransferred &&
                !estPremierRang(commande, rang) &&
                (sid === StatusId.BrouillonAcheteur || sid === StatusId.AAnalyser);

              if (isTransferred && !isTransfertAcheteurValidation) {
                const reponseEvent = (commande.historique || [])
                  .filter((h) => h.prefix === 'Consultation')
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.updated_at || b.created_at || '').getTime() -
                      new Date(a.updated_at || a.created_at || '').getTime(),
                  )[0];
                return (
                  <div className="prestataire-mode">
                    <div className="message-content">
                      <h4>Consultation envoyée au prestataire de rang {rang}</h4>
                      <p>
                        Le prestataire a reçu votre consultation. Vous serez informé de sa réponse dans les 10 jours
                        maximum.
                      </p>
                      <div className="status-indicator">
                        <span className="status-dot"></span>
                        Consultation reçue le{' '}
                        {reponseEvent ? formatDate(reponseEvent.updated_at || reponseEvent.created_at || '') : 'N/A'}
                      </div>
                    </div>
                  </div>
                );
              }

              if (adminReadOnly) {
                if (hasExistingArticles) {
                  return (
                    <div className="articles-display">
                      <div className="articles-table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>N° Réf</th>
                              <th>Description de la prestation</th>
                              <th>Unité</th>
                              <th>Quantité</th>
                              <th>Prix unitaire HT</th>
                              <th>Total HT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {existingArticles.map((article) => (
                              <tr key={article.id}>
                                <td>{article.code}</td>
                                <td>{article.nom}</td>
                                <td>{article.unite}</td>
                                <td>{article.quantite}</td>
                                <td>{formatPrice(article.prix, 4, true)}</td>
                                <td>{formatPrice(article.totalHT, 4, true)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="total-section">
                        <p>
                          Total HT (Rang {rang}) :{' '}
                          {formatPrice(existingArticles.reduce((sum, a) => sum + a.totalHT, 0), 4, true)}
                        </p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="no-articles-message">
                    <p>Aucun article défini pour cette consultation.</p>
                  </div>
                );
              }

              // Mode edition (brouillon acheteur)
              return (
                <>
                  {/* Message d'information de la section, rendu EN PERMANENCE tant que l'acheteur peut
                      composer — y compris quand des articles sont deja retenus. Il vivait auparavant
                      DANS le bloc des trois voies, donc il disparaissait au premier article ajoute,
                      alors que la consigne reste valable pendant toute la composition. */}
                  <Banner
                    variant="info"
                    className="catalogue-choix-info"
                    message="Veuillez sélectionner les prestations qui vous intéressent et indiquer les quantités souhaitées. Si vous préférez laisser le titulaire établir la proposition, vous pouvez cliquer sur « Laisser le titulaire proposer un chiffrage » ou « Charger la proposition du titulaire » (si vous disposez déjà de son CODE chiffrage)."
                  />

                  {/* Les 3 voies sont placees JUSTE APRES la banniere d'information : c'est la
                      consigne qui les annonce. Elles restent visibles meme quand un panier est
                      compose — elles ont remplace le bouton « Changer de mode ». */}
                  {actions.choisirCatalogue.visible && (
                    <>
                    {/* Les 3 voies sont offertes ensemble, sur une rangee. L'acheteur bascule
                        librement entre la 1re et la 3e tant qu'il n'a pas lance la consultation.
                        Seule la 2e engage — elle passe donc par sa confirmation d'irreversibilite,
                        et n'est jouee qu'une fois confirmee.
                        La 3e n'a pas de bouton qui la revele : l'input EST la voie, le placeholder
                        l'enonce. Un bouton pour ouvrir un champ etait un clic sans decision. */}
                    <div className="catalogue-choix">
                      <Button
                        value="Je sélectionne les articles et les quantités"
                        variant="secondary"
                        onClick={() => demanderMode('catalogue')}
                      />
                      <Button
                        value="Je laisse le titulaire proposer un chiffrage"
                        // Reste `secondary` : l'orange est reserve a la voie du code, qui porte une
                        // action. Ce choix-ci est retenu, pas actionne — d'ou un simple etat
                        // "selectionne" (cf. .catalogue-choix-btn--retenu).
                        variant="secondary"
                        className={articlesMode === 'prestataire' ? 'catalogue-choix-btn--retenu' : undefined}
                        // Intention seulement : aucun appel reseau. Seul « Lancer la consultation »
                        // engage l'operation. La confirmation ne surgit que si un panier existe deja
                        // et serait detruit par la bascule.
                        onClick={() => demanderMode('prestataire')}
                      />

                      {/* Troisieme voie : input + bouton soudes, un seul objet ORANGE. C'est lui qui
                          prend le reste de la rangee — il porte deux elements la ou les autres n'ont
                          qu'un libelle, d'ou le placeholder complet : il ENONCE la voie, comme les
                          deux boutons voisins enoncent la leur.
                          Appliquer le code remplit reellement l'operation avec l'offre du titulaire —
                          l'apercu laisse alors place au catalogue charge. */}
                      {/* `--pret` porte l'etat DU BOUTON sur tout l'objet : desactive, le Button se
                          fane seul (opacity .5), mais l'input garderait un orange plein et la
                          soudure se verrait. `codeButtonDisabled` est calcule une fois et sert aux
                          deux — deux expressions equivalentes finiraient par diverger. */}
                      <div
                        className={`catalogue-choix-code${codeButtonDisabled ? '' : ' catalogue-choix-code--pret'}`}
                      >
                        <input
                          type="text"
                          className="catalogue-choix-code-input"
                          placeholder="Je dispose déjà du chiffrage (mettre le code)"
                          value={codeChiffrage}
                          // Un code fait exactement CODE_LONGUEUR caracteres, en majuscules, pris
                          // dans un alphabet sans caractere ambigu. On normalise a la saisie plutot
                          // que de refuser apres coup : taper en minuscules, ou coller un code avec
                          // des espaces, doit marcher.
                          onChange={(e) => {
                            setCodeChiffrage(
                              e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, CODE_LONGUEUR),
                            );
                            if (codeChiffrageError) setCodeChiffrageError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleChargerChiffrage();
                          }}
                          maxLength={CODE_LONGUEUR}
                          disabled={applyingCode}
                          // Le placeholder est abrege pour tenir : l'intitule complet de la voie vit
                          // ici, pour qui navigue au lecteur d'ecran.
                          aria-label="Je dispose déjà du chiffrage, saisir le code"
                        />
                        <Button
                          // Libelle court : dans le tiers, "Charger la proposition du titulaire"
                          // mangeait la place de l'input, qui prime. La banniere d'info porte deja
                          // la formulation complete, et `title` la redonne au survol.
                          value={applyingCode ? 'Chargement...' : 'Charger'}
                          title="Charger la proposition du titulaire"
                          variant="primary"
                          onClick={handleChargerChiffrage}
                          disabled={codeButtonDisabled}
                        />
                      </div>
                    </div>

                    {codeChiffrageError && (
                      <Banner variant="danger" className="catalogue-choix-info" message={codeChiffrageError} />
                    )}

                    </>
                  )}

                  {hasExistingArticles && (
                    <div className="articles-table-container">
                      <div className="articles-table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>N° Réf</th>
                              <th>Description de la prestation</th>
                              <th>Unité</th>
                              <th>Quantité</th>
                              <th>Prix unitaire HT</th>
                              <th>Total HT</th>
                              {actions.changerMode.visible && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {existingArticles.map((article) => (
                              <tr key={article.id}>
                                <td className="article-number">{article.code}</td>
                                <td className="article-description">
                                  <div className="article-name">{article.nom}</div>
                                </td>
                                <td className="article-unit">{article.unite}</td>
                                <td className="article-quantity">
                                  <div className="quantity-cell">{article.quantite}</div>
                                </td>
                                <td className="article-price price-cell">
                                  {formatPrice(ArticlesService.getPrixByQuantity(article, article.quantite), 4, true)}
                                </td>
                                <td className="article-total total-cell">{formatPrice(article.totalHT, 4, true)}</td>
                                {actions.changerMode.visible && (
                                  <td className="article-actions">
                                    <button
                                      className="delete-button"
                                      onClick={() => handleRemoveExistingArticle(article.catalogueId || article.id)}
                                      type="button"
                                      disabled={deletingId !== null}
                                    >
                                      {deletingId === (article.catalogueId || article.id) ? (
                                        <span className="delete-spinner" aria-label="Suppression en cours" />
                                      ) : (
                                        '×'
                                      )}
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {actions.ajouterArticle.visible && (
                          <button
                            className="add-article-button"
                            type="button"
                            onClick={() => setIsArticlesModalOpen(true)}
                          >
                            Ajouter ou modifier un article
                          </button>
                        )}
                      </div>

                      <div className="articles-actions">
                        <div className="total-section">
                          <p>
                            Total HT (Rang {rang}):{' '}
                            {formatPrice(existingArticles.reduce((sum, a) => sum + a.totalHT, 0), 4, true)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* La preview reste montee tant que la modale est ouverte (|| isArticlesModalOpen) :
                      ouvrir la modale bascule articlesMode et masquerait sinon le bloc derriere elle. */}
                  {/* La preview reste montee tant que la modale est ouverte (|| isArticlesModalOpen) :
                      ouvrir la modale bascule articlesMode et masquerait sinon le bloc derriere elle.
                      Preview affichee DIRECTEMENT (pas de titre ni de box grise) : un clic n'importe ou
                      ouvre la modale de composition. */}
                  {/* Les 3 voies restent affichees EN PERMANENCE, articles composes ou non : elles
                      remplacent le bouton « Changer de mode », qui obligeait a repasser par un ecran
                      intermediaire pour changer d'avis. Basculer alors qu'un panier existe detruit
                      les articles : le clic passe donc par la confirmation (cf. `demanderMode`). */}
                  {/* Apercu / message d'attente : uniquement TANT QU'AUCUN article n'est retenu.
                      Des qu'une selection existe, c'est le tableau des articles ci-dessus qui la
                      montre — afficher les deux ferait doublon. */}
                  {actions.choisirCatalogue.visible && !hasExistingArticles && (
                    <>
                    {articlesMode === 'prestataire' ? (
                      // Choix 2 retenu : l'apercu du catalogue n'a plus de sens, ce n'est plus
                      // l'acheteur qui compose. Rien n'est encore envoye — le titulaire ne sera
                      // informe qu'au lancement, et le message le dit tel quel.
                      <div className="catalogue-attente-titulaire">
                        Le titulaire sera informé qu'il doit vous proposer un chiffrage dès que
                        vous lancerez la consultation.
                      </div>
                    ) : (
                      /* Apercu rendu par LE catalogue lui-meme (ModalArticles en `inline`), et non par
                         une copie de ses lignes : 5 lignes + "+ X autres articles", tout en lecture
                         seule. Le conteneur porte le clic (il ouvre la vraie modale) et neutralise les
                         interactions internes via CSS. */
                      <div
                        className="catalogue-preview-inline"
                        role="button"
                        tabIndex={0}
                        title="Ouvrir le catalogue"
                        onClick={() => {
                          setArticlesMode('catalogue');
                          setIsArticlesModalOpen(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setArticlesMode('catalogue');
                            setIsArticlesModalOpen(true);
                          }
                        }}
                      >
                        <ModalArticles
                          isOpen
                          inline
                          readOnly
                          previewLimit={5}
                          commandes={cataloguePreviewCommandes}
                          lotNom={commande.operation?.lot_technique?.nom ?? commande.lot_technique?.nom ?? ''}
                          prestataireRang={1}
                          onAddToCommand={() => {}}
                          onClose={() => {}}
                        />
                      </div>
                    )}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </SectionPliable>

        {/* Descriptif procédural : recap lecture seule (parité MS). Les champs proceduraux propres au
            MS (ouverture des plis, notification, budget, origine, Q/R, criteres) ne s'appliquent PAS au
            bon de commande : ne sont repris que l'accord-cadre, les lots, la date limite et la sous-traitance. */}
        <SectionPliable title="Descriptif procédural" defaultOpen>
          {/* Portee de la date de validite affichee juste en dessous : un bon notifie avant le terme
              de l'accord-cadre reste executable apres. Texte reglementaire partage (une seule
              redaction pour les vues acheteur et prestataire). */}
          <Banner
            variant="info"
            className="bc-mention-procedural"
            message={MENTION_VALIDITE_ACCORD_CADRE_BC}
          />
          <div className="section-content">
            <div className="bcadm-desc">
              <div className="bcadm-desc__grid">
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Accord-cadre en vigueur</label>
                  <div className="input-inactif">Accord-cadre édition 2025-2026</div>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Date de validité</label>
                  <div className="input-inactif">01/01/2025 au 31/12/2026</div>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Lot technique</label>
                  <div className="input-inactif">{procLotTechnique || '-'}</div>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Lot géographique</label>
                  <div className="input-inactif">{procLotGeographique || '-'}</div>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Date et heure limite de réception des offres</label>
                  <div className="input-inactif">{procDateLimite || '-'}</div>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Durée de consultation (jours calendaires)</label>
                  <div className="input-inactif">10 jours</div>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Sous-traitance autorisée</label>
                  <div className="input-inactif">DC4 à présenter après notification</div>
                </div>
              </div>
            </div>

            {/* Titulaire : UNIQUEMENT le rang consulté (pas les 3). La carte est cherchee par l'ENTITE de
                la commande de ce rang, pas par numero de rang dans l'annuaire : l'annuaire donne le
                titulaire ACTUEL du rang, qui peut changer (inversion de rangs) alors que la commande
                reste liee a son titulaire. */}
            <div className="form-group" style={{ marginTop: '2rem' }}>
              <label>Titulaire du rang consulté</label>
              <TitulairesLotCards
                lotTechnique={procLotTech?.id}
                departement={procDep?.id}
                mode="BC"
                rang={rang}
                entiteId={commandeCeRang?.id_entite}
              />
            </div>

            {/* Documents liés à la consultation : pièces de l'accord-cadre + liste des pièces (miroir). */}
            <div className="form-group" style={{ marginTop: '2rem' }}>
              <label>Documents liés à la consultation</label>
              {acDocsLoading && piecesAccordCadre.length === 0 ? (
                <div className="input-inactif">Chargement des pièces…</div>
              ) : (
                <DocumentsList
                  documents={piecesAccordCadre}
                  variant="grid"
                  // Une seule place, comme un document : la carte reste sur la ligne des autres.
                  trailingCardSpan={1}
                  trailingCard={
                    <ListePiecesCard
                      texte={formData.listePiecesDocumentation as string | undefined}
                    />
                  }
                />
              )}
            </div>
          </div>
        </SectionPliable>

        {/* Encart consultation lancee / transferee */}
        {consultationStatus.isVisible && (
          <section>
            {/* « Consultation lancée … » deplacee en banniere en haut (voir plus haut). */}
            {consultationStatus.showOffreEnvoyee && (
              <div className="section-content consultation-info">
                <p className="offre-envoyee-message">
                  {`Offre envoyée le ${commande.updated_at ? formatDate(commande.updated_at) : 'date non disponible'} par le prestataire ${commandeCeRang?.contrat?.entite?.nom || 'Prestataire'} de rang ${rang}`}
                </p>
              </div>
            )}
          </section>
        )}

        {/* Message d'annulation */}
        {isAnnule && (
          <section>
            <div className="section-content consultation-info">
              <p>
                {(() => {
                  const ev = (commande.historique || []).find((h) => h.prefix === 'Annulation');
                  if (!ev) return 'Projet de bon de commande annulé';
                  const d = new Date(ev.created_at || ev.updated_at || '');
                  const dateStr = d.toLocaleDateString('fr-FR');
                  const timeStr = `${d.getHours().toString().padStart(2, '0')}h${d.getMinutes().toString().padStart(2, '0')}m${d.getSeconds().toString().padStart(2, '0')}s`;
                  return `Projet de bon de commande annulé le ${dateStr} à ${timeStr} par l'acheteur`;
                })()}
              </p>
            </div>
          </section>
        )}

        {/* Rappel de l'etat juste avant la barre d'actions : l'onglet est long (descriptifs admin,
            technique et financier), l'acheteur ne doit pas remonter pour savoir ou il en est.
            Meme JSX qu'en tete, defini une seule fois. */}
        {stateBanners}

        {/* Boutons d'actions */}
        <div className="action-buttons">
          {showActionRow && (
            <div className="action-buttons-group">
              <div className="action-buttons-row">
                {actions.annuler.visible && (
                  <Button
                    value={isDeleting ? 'Annulation...' : 'Annuler la consultation'}
                    variant="cancel"
                    onClick={() => setIsCancelModalOpen(true)}
                    disabled={actions.annuler.disabled || isDeleting || isSaving}
                  />
                )}
                {actions.sauvegarderQuitter.visible && (
                  <Button
                    value="Sauvegarder et quitter"
                    variant="secondary"
                    onClick={handleSaveAndQuit}
                    disabled={actions.sauvegarderQuitter.disabled || isSaving}
                  />
                )}
                {actions.refuserOffre.visible && (
                  <Button
                    value={`Refuser l'offre (Rang ${rang})`}
                    variant="secondary"
                    onClick={() => handleRefuserCommande(rang)}
                    disabled={actions.refuserOffre.disabled}
                  />
                )}
                {printButton}
                {actions.consulterRangSuivant.visible && (
                  <Button
                    value={`Consulter le prestataire de rang ${rang}`}
                    variant="primary"
                    onClick={() => {
                      setPendingRangToConsult(rang);
                      setIsConfirmConsultationModalOpen(true);
                    }}
                    disabled={actions.consulterRangSuivant.disabled}
                  />
                )}
                {actions.demanderValidation.visible && (
                  <Button
                    value="Obtenir la validation de l'acheteur signataire"
                    variant="primary"
                    onClick={handleNotifyResponsable}
                    disabled={actions.demanderValidation.disabled || isSaving}
                    title={actions.demanderValidation.reason ? launchTooltip : ''}
                  />
                )}
                {actions.lancerConsultation.visible && (
                  <Button
                    value={`Lancer la consultation (Rang ${rang})`}
                    variant="primary"
                    onClick={handleLancer}
                    disabled={actions.lancerConsultation.disabled || isSaving}
                    title={launchTooltip}
                  />
                )}
                {actions.accepterOffre.visible && (
                  <Button
                    value={`Accepter l'offre (Rang ${rang})`}
                    variant="primary"
                    onClick={() => handleAccepterCommande(rang)}
                    disabled={actions.accepterOffre.disabled}
                  />
                )}
                {nextTab && (
                  <NextTabButton
                    label={nextTab.label}
                    hidden={!nextTab.visible}
                    // `no-print` : de la navigation n'a rien a faire sur un document imprime, et le
                    // ref d'impression de « Imprimer la consultation » couvre tout l'onglet. La
                    // classe est effective via la feuille @media print injectee par PrintButton.
                    className="no-print"
                    onClick={nextTab.onClick}
                  />
                )}
              </div>
              {actions.demanderValidation.visible && notificationInfo.sent && notificationInfo.labels.length > 0 && (
                <div className="notifications-sent-list">
                  {notificationInfo.labels.map((label, index) => (
                    <div key={index} className="notification-sent-info">
                      <span className="notification-sent-icon">✓</span>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modales */}
        {isArticlesModalOpen && (
          <ModalArticles
            isOpen={isArticlesModalOpen}
            onClose={() => {
              setIsArticlesModalOpen(false);
              if (existingArticles.length === 0) setArticlesMode('initial');
            }}
            commandes={commande.commandes || []}
            lotNom={commande.lot_technique?.nom || 'Catalogue'}
            onAddToCommand={handleAddToCommand}
            existingSelection={existingArticles}
            prestataireRang={rang}
            // Ni "laisser le titulaire" ni le code de chiffrage ici : ces deux voies sont des choix de
            // composition, pas des gestes de catalogue. Elles vivent au-dessus de l'apercu, avec la
            // troisieme, pour que les trois soient offertes ensemble.
          />
        )}

        <CGVAccept
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          isOpen={isCGVModalOpen}
          onClose={() => {
            setIsCGVModalOpen(false);
            setReadyToLaunchAfterCGV(false);
          }}
          onConfirm={() => {
            setAcceptCGV(true);
            setTimeout(() => {
              setIsCGVModalOpen(false);
              // Une seule reprise possible apres les CGV : le lancement. La branche "transfert" est
              // partie avec sa modale — le transfert passe desormais par ce meme lancement.
              if (readyToLaunchAfterCGV) {
                setReadyToLaunchAfterCGV(false);
                handleConfirmLaunch();
              }
            }, 100);
          }}
        />

        <ValidationPopUp
          isOpen={isConfirmModalOpen}
          title="Lancement de la consultation"
          text={
            articlesMode === 'prestataire'
              ? `Vous vous apprêtez à lancer la consultation pour le prestataire de rang ${rang}, qui vous proposera un chiffrage. Vous ne pourrez plus revenir sur le descriptif Administratif et Technique. Cette action est irréversible.`
              : `Vous vous apprêtez à lancer la consultation pour le prestataire de rang ${rang}. Cette action est irréversible.`
          }
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={async () => {
            setIsConfirmModalOpen(false);
            await lancerSelonMode();
          }}
          cancelLabel="Annuler"
          confirmLabel="Confirmer"
        />

        <ValidationPopUp
          isOpen={isConfirmConsultationModalOpen}
          title={`Consulter le prestataire de rang ${pendingRangToConsult ?? ''}`}
          text={`Êtes-vous sûr de vouloir lancer la consultation pour le prestataire de rang ${pendingRangToConsult ?? ''} ?`}
          onClose={() => {
            setIsConfirmConsultationModalOpen(false);
            setPendingRangToConsult(null);
          }}
          onConfirm={handleConfirmConsultationRangSuivant}
          cancelLabel="Non"
          confirmLabel="Oui"
        />

        {/* La modale "Envoyer une demande au prestataire" a ete retiree : le transfert n'est plus une
            action a part avec sa propre confirmation. Il est devenu ce que "Lancer la consultation"
            signifie quand l'acheteur a choisi de laisser le titulaire proposer — un seul point
            d'engagement pour les trois voies, donc une seule confirmation (celle du lancement). */}

        <ValidationPopUp
          isOpen={isCancelModalOpen}
          title="Annulation de la commande"
          text={
            commande.status?.id === StatusId.BrouillonAcheteur
              ? 'Êtes-vous sûr de vouloir annuler et supprimer cette commande ? Cette action est irréversible.'
              : "Êtes-vous sûr de vouloir annuler cette commande ? L'opération et les commandes liées passeront en statut annulé. Cette action est irréversible."
          }
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelOperation}
          cancelLabel="Non"
          confirmLabel="Oui, annuler"
        />

        <ValidationPopUp
          isOpen={isChangeModeModalOpen}
          title={titreChangementMode}
          text={messageChangementMode}
          onClose={() => {
            setIsChangeModeModalOpen(false);
            // La demande est ABANDONNEE avec la modale : la laisser armee ferait basculer vers une
            // voie que l'acheteur vient de refuser si une autre confirmation survenait ensuite.
            setModeDemande(null);
          }}
          onConfirm={handleConfirmModeChange}
          cancelLabel="Annuler"
          confirmLabel="Confirmer"
        />

        <ValidationPopUp
          isOpen={isNotifyResponsableModalOpen}
          title="Obtenir la validation de l'acheteur signataire"
          text=""
          onClose={() => {
            setIsNotifyResponsableModalOpen(false);
            setSelectedResponsable(null);
          }}
          onConfirm={handleConfirmNotifyResponsable}
          cancelLabel="Annuler"
          confirmLabel="Envoyer la demande de validation"
        >
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionnez le responsable à qui envoyer la demande :
            </label>
            {responsablesLoading ? (
              <p className="text-gray-500">Chargement...</p>
            ) : responsables.length === 0 ? (
              <p className="text-red-500">Aucun responsable disponible</p>
            ) : (
              <select
                style={{ marginTop: '16px', marginBottom: '24px' }}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedResponsable ? `${selectedResponsable.type}-${selectedResponsable.id}` : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const [type, id] = value.split('-');
                    setSelectedResponsable({ id: parseInt(id, 10), type });
                  } else {
                    setSelectedResponsable(null);
                  }
                }}
              >
                <option value="">-- Choisir un responsable --</option>
                {responsables.map((resp) => (
                  <option key={`${resp.type}-${resp.id}`} value={`${resp.type}-${resp.id}`}>
                    {`${(resp.prenom || '').charAt(0).toUpperCase() + (resp.prenom || '').slice(1).toLowerCase()} ${(resp.nom || '').toUpperCase()}`}{' '}
                    - {resp.type === 'responsable_achat' ? 'Administrateur' : 'Signataire'}
                    {resp.fonction ? ` (${resp.fonction})` : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedResponsable && (
              <p className="mt-2 text-sm text-gray-600">
                Email :{' '}
                {
                  responsables.find(
                    (r) => r.id === selectedResponsable.id && r.type === selectedResponsable.type,
                  )?.email_contact
                }
              </p>
            )}
          </div>
        </ValidationPopUp>
      </div>
    </>
  );
};

export default OperationConsultation;
